import type { RecentVisit } from '@/hooks/useSuperStats';

interface RecentVisitorsProps {
  items: RecentVisit[];
  loading: boolean;
}

const KIND_ICON: Record<string, string> = {
  'Site web': 'ri-global-line',
  'Page produit': 'ri-shopping-bag-3-line',
  'Page service': 'ri-briefcase-4-line',
  'Accueil Zifek': 'ri-home-8-line',
};

function formatDate(value: string): string {
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Derniers visiteurs détectés sur la plateforme (page, boutique, IP masquée).
 */
export default function RecentVisitors({ items, loading }: RecentVisitorsProps) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 h-full">
      <h3 className="text-sm font-semibold text-foreground-900 flex items-center gap-2 mb-4">
        <i className="ri-map-pin-user-line text-secondary-700"></i>
        Derniers visiteurs
      </h3>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <i className="ri-loader-4-line animate-spin text-2xl text-foreground-300"></i>
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-foreground-400">
          <i className="ri-map-pin-user-line text-3xl mb-2"></i>
          <p className="text-sm">Aucun visiteur récent</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
          {items.map((visit) => (
            <div
              key={visit.id}
              className="flex items-center gap-3 py-2 border-b border-background-200/60 last:border-0"
            >
              <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                <i className={KIND_ICON[visit.kind] || 'ri-eye-line'}></i>
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-foreground-900 truncate">{visit.boutique}</div>
                <div className="text-[11px] text-foreground-400 truncate">
                  {visit.kind}
                  {visit.source ? ` · ${visit.source}` : ''}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-[11px] text-foreground-500 whitespace-nowrap">
                  {formatDate(visit.date)}
                </div>
                <div className="text-[10px] text-foreground-400 whitespace-nowrap">
                  {visit.ip}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}